import Forms from './../../utils/forms/forms';
import { mapGetters, mapMutations } from 'vuex';
import productService from './../../services/product';
import orderProductService from './../../services/orderProduct';
import billProductService from './../../services/billProduct';
import taxService from './../../services/tax';
import productDepositService from './../../services/productDeposit';
import store from './../../store';
import QuickProductEdit from './../../components/quickProductEdit/quickProductEdit.vue';
import BundleProductPreview from './../../components/bundleProductPreview/bundleProductPreview.vue';
import organizationSettingsService from './../../services/organizationSettings';
import bulkAddProductModal from './../../components/bulkAddProductModal/bulkAddProductModal.vue';

export default {
  data() {
    return {
      form: new Forms({
        ref: {
          value: '',
          type: 'link',
          attributes: {
            href: '',
            target: '',
            fieldClass: '',
          },
        },
        orderProductId: {
          value: '',
          type: 'autocomplete',
          attributes: {
            isSearch: true,
            searchMethod: this.searchMethod,
            loading: true,
          },
        },
        id: {
          value: '',
          type: 'text',
        },
        price: {
          value: '',
          type: 'float',
        },
        productDepositName: {
          value: '',
          type: 'plaintext',
        },
        quantity: {
          value: '',
          type: 'float',
        },
        discount: {
          value: '',
          type: 'number',
        },
        taxId: {
          value: '',
          type: 'select',
        },
      }),
      hiddenFields: [],
      destroyLoading: false,
      addingProduct: false,
      editingProduct: false,
      error: false,
      ready: false,
      columnWidths: { ref: '150px' },
      additionalButtons: [{
        text: this.$t('table.orderProducts.addProductsButton'),
        action: this.showBulkAddProductModal,
      }],
    };
  },

  props: {
    /**
     * The current order.
     */
    order: {
      type: Object,
      required: true,
    },

    /**
     * The current credit note.
     */
    bill: {
      type: Object,
      required: true,
    },

    /**
     * The current order.
     */
    billRoundInvoiceTotal: {
      type: Boolean,
      required: false,
    },
  },

  computed: {
    ...mapGetters({
      products: 'allProducts',
      orderProducts: 'allOrderProducts',
      billProducts: 'allBillProducts',
      taxes: 'allTaxes',
      productDeposits: 'allProductDeposits',
    }),

    billProductsList() {
      const links = {};
      const products = this.billProducts.map((p) => {
        let deposit = [];
        if (p.productDepositId) {
          deposit = this.productDeposits.all
            .filter(d => p.productDepositId.toString() === d.id.toString());
        }

        let value = '';
        if (deposit.length) {
          value = `Deposit: ${deposit[0].name}`;
        }

        const addClass = `link-table-${p.refNumber}-${p.refId}`;
        let linkDisable = false;
        if (p.refNumber === '--') linkDisable = true;

        links[p.id] = `#/orders/${p.refId}/edit`;
        this.form.setExtraAttr('ref', {
          href: links[p.id],
          hrefs: links,
          target: '_blank',
          fieldClass: addClass,
          fieldDisable: linkDisable, // (temp disabled due to loop error) linkDisable,
        });
        const base = {
          valueNote: p.note,
          productDepositName: value,
          orderProductId: p.orderProductId,
          orderProductDeposit: p.productDeposit,
          ref: (p.refNumber === '--') ? '--' : `${this.$t('static.referenceSign.order')}-${p.refNumber}`,
        };
        if (p.taxPercentage !== undefined && p.taxPercentage !== null) {
          base.taxIdDisplay = p.taxPercentage;
        }
        return Object.assign(base, p);
      });

      return products;
    },

    totalsTableList() {
      const fmt = val => Number(val).toLocaleString('nb-NO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const result = [
        {
          name: this.$t('static.orders.edit.subtotal'),
          value: fmt(this.subtotal),
        },
        {
          name: this.$t('static.orders.edit.tax'),
          value: fmt(this.tax),
        },
      ];

      if (this.discount > 0) {
        result.push({
          name: this.$t('static.orders.edit.discount'),
          value: fmt(this.discount),
        });
      }

      if (this.deposit > 0) {
        result.push({
          name: this.$t('static.orders.edit.deposit'),
          value: fmt(this.deposit),
        });
      }

      if (this.penaltyFee > 0) {
        result.push({
          name: 'Penalty fee',
          value: fmt(this.penaltyFee),
        });
      }

      result.push({
        name: this.$t('static.orders.edit.total'),
        value: fmt(this.total),
      });

      return result;
    },

    /**
     * If bill status is sent or higher.
     */
    isBillSent() {
      return this.bill.billStatus !== 'Draft' &&
        this.bill.billStatus !== 'Pending';
    },

    /**
     * Whether table was shown in a bill and round invoice total is true
     */
    isShownOnBill() {
      return this.billRoundInvoiceTotal;
    },

    /**
     * Whether table should appear or not.
     */
    showTable() {
      if (this.billProductsList.length > 0 || this.addingProduct || this.editingProduct) {
        return true;
      }

      return false;
    },

    subtotal() {
      return this.billProductsList.map(p => (
          p.price * p.quantity
        )).reduce((a, b) => a + b, 0)
        .toFixed(2);
    },

    discount() {
      return this.billProductsList.map((p) => {
        const op = this.getOrderProduct(p.orderProductId);
        return (p.price ? p.price : op.price) * p.quantity * (p.discount / 100);
      }).reduce((a, b) => a + b, 0).toFixed(2);
    },

    penaltyFee() {
      return this.bill.reminderFeesTotalAmount.toFixed(2);
    },

    /* eslint max-len: ["error", 150] */
    tax() {
      return this.billProductsList.map((p) => {
        const op = this.getOrderProduct(p.orderProductId);
        return (p.price ? p.price : op.price) * p.quantity * (1 - (p.discount / 100)) * ((p.taxId ? p.taxPercentage : op.taxPercentage) / 100);
      }).reduce((a, b) => a + b, 0).toFixed(2);
    },

    deposit() {
      return this.billProductsList.map(p => (
          p.orderProductDeposit * p.quantity
        )).reduce((a, b) => a + b, 0)
        .toFixed(2);
    },

    total() {
      const total = ((Number(this.subtotal) + Number(this.deposit)) - Number(this.discount)) + Number(this.penaltyFee);
      const totalTax = total + Number(this.tax);

      if (this.isShownOnBill) {
        return Math.round(totalTax).toFixed(2);
      }

      return totalTax.toFixed(2);
    },

    /**
     * The rounding for the total.
     */
    rounding() {
      const total = Number(this.subtotal - this.discount);
      const totalTax = total + Number(this.tax);
      const roundedDecimal = (totalTax % 1).toFixed(2);

      if (roundedDecimal < 0.50) {
        return -roundedDecimal;
      }

      return roundedDecimal;
    },
  },

  watch: {
    /**
     * Watch product select for autocomplete other fields with defaults.
     */
    'form.orderProductId': function () {
      const productId = typeof this.form.orderProductId.id !== 'undefined' ? this.form.orderProductId.id : this.form.orderProductId;
      if (productId > 0) {
        const orderProduct = this.getOrderProduct(productId);
        const billProduct = this.billProducts.find(p => +p.id === +this.form.id);
        if (typeof orderProduct !== 'undefined') {
          const tax = this.taxes.all.filter(t => (
            t.id === orderProduct.taxId
          ));

          if (tax.length > 0) {
            this.form.taxId = tax[0].id;
          }
          this.form.price = orderProduct.price;
          this.form.quantity = billProduct.quantity || orderProduct.quantity;
          // this.form.orderProductId = {'name':orderProduct.name, id: productId};
        }
      }
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} products    The order products list.
     */
    orderProducts(products) {
      this.form.setOptions('orderProductId', products);
      if (this.order.client.clientType.id === 11) {
        this.hiddenFields.push('taxId');
      }
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} products    The products list.
     */
    productDeposits(deposits) {
      this.form.setOptions('productDepositId', deposits.all);
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} taxes    The taxes list.
     */
    taxes(taxes) {
      this.form.setOptions('taxId', taxes.all.map(tax => ({ id: tax.id, name: tax.taxPercentage })));
    },
  },
  created() {
    this.hiddenFields.push('id');
    Promise.all([organizationSettingsService.show()]).then((data) => {
      this.form.setDisplays('orderProductId', data[0].productInfoDisplay);
    }).catch((errors) => {});
  },
  methods: {
    ...mapMutations({
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalProps: 'SET_MODAL_PROPS',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
    }),

    showBulkAddProductModal() {
      this.setModalContent(() => bulkAddProductModal);
      this.setModalProps({
        fetchProductsMethod: this.searchMethod,
        onSave: this.handleBulkProductSave,
      });
      this.toggleModal(true);
    },

    handleBulkProductSave(orderProducts) {
      const id = this.bill.id;
      const data = orderProducts.map(p => ({
        orderProductId: p.id,
        quantity: p.qty,
        price: p.price,
      }));

      return billProductService.bulkStore(id, data)
        .then(() => {
          this.form.loading = false;
        });
    },

    getDepositValue(id) {
      const deposits = this.productDeposits.all.filter(d => d.id.toString() === id.toString());
      return deposits.length ? deposits[0].amount : 0;
    },

    /**
     * Get the order product for a credit note product.
     *
     * @param {Number} productId The orderProductId.
     */
    getOrderProduct(productId) {
      return this.orderProducts.filter(p => (
        p.id.toString() === productId.toString()
      ))[0];
    },

    /**
     * This will be called when the form emit a change event.
     *
     * @param  {String} name    The input name.
     * @param  {String} value    The input value.
     */
    onFormChange(name, value) {
      this.form.clearSuccess(name, value);
      this.form.clearErrors(name);
      this.form[name] = value;
      this.$forceUpdate();
    },

    /**
     * Opens the form to add a product.
     */
    addProduct() {
      this.cancelForm();
      this.addingProduct = true;
    },

    /**
     * Action for editing a order product.
     */
    edit(product) {
      this.cancelForm();
      this.editingProduct = product.id;
      this.form.assignData(product);
    },

    /**
     * Closes the form, canceling inputs.
     */
    cancelForm() {
      this.form.orderProductId = '';
      this.form.quantity = '';
      this.addingProduct = false;
      this.editingProduct = false;
    },

    /**
     * Updates a order product.
     */
    updateOrderProduct(orderProductId, data) {
      data.orderProductId = typeof data.orderProductId.id !== 'undefined' ? data.orderProductId.id : data.orderProductId;

      return orderProductService.update(this.order.id, data, orderProductId)
        .then(() => {
          orderProductService.all(this.order.id);
          billProductService.all(this.bill.id);
        }).catch((errors) => {
          const errorsKeys = Object.keys(this.form.errors.errors);
          this.$message({
            showClose: true,
            message: errors[errorsKeys[0]][0],
            customClass: 'error',
          });
        });
    },

    /**
     * The handler for the quick product edit component that will be passed to the modal.
     *
     * @param  {Object} item The row item.
     * @return {Object}      The component.
     */
    onQuickProductEditMounted() {
      const that = this;
      return function () {
        this.$once('close', () => {
          productService.all().then(() => {
            const filteredProducts = that.products
              .filter(p => p.id.toString() === this.orderProduct.productId.toString());
            if (filteredProducts.length) {
              that.form.assignData(this.orderProduct);
              that.form.assignData(filteredProducts[0]);
              that.updateOrderProduct(this.orderProduct.id, Object.assign({}, this.orderProduct, filteredProducts[0]))
                .then(() => {
                  that.form.resetValues();
                });
            }
          });
        });
      };
    },

    /**
     * Defines the handler for the links one the table.
     *
     * @param  {Object} item The row item.
     * @param  {String} name The field name.
     * @return {Mixed}      False or the handler function in case of a link needed.
     */
    tableClickHandler(item, name) {
      const ops = this.orderProducts.filter(op => op.id.toString() === item.orderProductId.toString());
      if (!this.order.sent && ops.length && ops[0].productId && name === 'orderProductId') {
        return () => {
          if (item.productType === 'Bundle') {
            this.setModalProps({
              productId: ops[0].productId,
              orderProduct: ops[0],
            });
            this.setModalContent(() => BundleProductPreview);
          } else {
            this.setModalProps({
              productId: ops[0].productId,
              orderProduct: ops[0],
              onMounted: this.onQuickProductEditMounted(),
            });
            this.setModalContent(() => QuickProductEdit);
          }
          this.toggleModal(true);
        };
      }

      return false;
    },

    /**
     * Saves the form, adding a product.
     */
    save() {
      const me = this;
      me.form.loading = true;
      const id = this.bill.id;
      const data = this.form.data();
      data.orderProductId = typeof data.orderProductId.id !== 'undefined' ? data.orderProductId.id : data.orderProductId;


      if (data.quantity.toString() === '0') {
        me.form.loading = false;
        me.$message({
          showClose: true,
          message: me.$t('static.orders.edit.quantityError'),
          type: 'error',
        });
        return Promise.all();
      }

      if (data.orderProductId.toString() === '') {
        me.form.loading = false;
        me.$message({
          showClose: true,
          message: me.$t('static.orders.edit.productErrorMessage'),
          type: 'error',
        });
        return Promise.all();
      }


      return billProductService.store(id, data)
        .then(() => {
          me.form.loading = false;
          data.orderProduct = this.orderProducts.filter(p => (
            p.id.toString() === data.orderProductId.toString()
          ))[0].name;
          store.dispatch('addBillProduct', data);
          me.cancelForm();
        }).catch((errors) => {
          me.form.loading = false;
          me.form.recordErrors(errors);
          const errorsKeys = Object.keys(this.form.errors.errors);
          me.$message({
            showClose: true,
            message: errors[errorsKeys[0]][0],
            type: 'error',
          });
        });
    },

    /**
     * Updates a order product.
     */
    update(orderProductId) {
      const me = this;
      this.form.loading = true;
      const id = this.bill.id;
      const data = this.form.data();
      data.orderProductId = typeof data.orderProductId.id !== 'undefined' ? data.orderProductId.id : data.orderProductId;

      if (data.quantity.toString() === '0') {
        me.form.loading = false;
        me.$message({
          showClose: true,
          message: me.$t('static.orders.edit.quantityError'),
          type: 'error',
        });
        return Promise.all();
      }

      if (data.orderProductId.toString() === '') {
        me.form.loading = false;
        me.$message({
          showClose: true,
          message: me.$t('static.orders.edit.productErrorMessage'),
          type: 'error',
        });
        return Promise.all();
      }
      return billProductService.update(id, data, orderProductId)
        .then(() => {
          me.form.loading = false;
          billProductService.all(id);
          data.orderProduct = me.orderProducts.filter(p => (
            p.id.toString() === data.orderProductId.toString()
          ))[0].name;
          store.dispatch('updateBillProduct', { billProduct: data, id: this.editingProduct });
          me.cancelForm();
        }).catch((errors) => {
          this.form.loading = false;
          this.form.recordErrors(errors);
          const errorsKeys = Object.keys(this.form.errors.errors);
          this.$message({
            showClose: true,
            message: errors[errorsKeys[0]][0],
            type: 'error',
          });
        });
    },

    /**
     * Delete the product from creditNote.
     */
    destroy(billProductId) {
      this.destroyLoading = billProductId;
      billProductService.destroy(this.bill.id, billProductId)
        .then(() => {
          this.destroyLoading = false;
        }).catch(() => {
          this.destroyLoading = false;
        });
    },

    getResources() {
      this.ready = false;
      Promise.all([
       // productService.all(),
        orderProductService.all(this.bill.orderId),
        billProductService.all(this.bill.id),
        taxService.all(),
        productDepositService.all(),
      ]).then(() => {
        this.ready = true;
      }).catch((error) => {
        this.ready = true;
      });
    },

    searchMethod(search, field) {
      return Promise.all([
        orderProductService.search(this.order.id, { search }),
      ]);
    },
  },

  mounted() {
    this.getResources();
  },

  components: {
    VFormTable: require('./../formTable/formTable.vue'),
    VTotalsTable: require('./../totalsTable/totalsTable.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/components/billProductsTable/billProductsTable.js